import httpClient from '@utils/httpClient';

// Direct database connection service
class DatabaseService {
    // Get all programs from database
    async getAllPrograms() {
        try {
            const response = await httpClient.get('/programs');
            console.log('📊 DatabaseService: Programs response:', response);
            return response.programs || [];
        } catch (error) {
            console.error('Error fetching programs:', error);
            return [];
        }
    }

    // Get recent programs for dashboard
    async getRecentPrograms(limit = 4) {
        try {
            const response = await httpClient.get(`/programs/recent?limit=${limit}`);
            console.log('📊 DatabaseService: Recent programs response:', response);
            return response.data || [];
        } catch (error) {
            console.error('Error fetching recent programs:', error);
            return [];
        }
    }

    // Get all programs as tasks from database (from programs table instead of program_progress)
    async getAllProgramProgress() {
        try {
            console.log('🔄 DatabaseService: Fetching programs from /programs...');
            const response = await httpClient.get('/programs');

            console.log('📊 DatabaseService: Programs response:', {
                success: response.success,
                dataLength: response.programs?.length || 0,
                hasData: !!response.programs,
                firstRecord: response.programs?.[0] || 'No records',
                lastRecord: response.programs?.[response.programs?.length - 1] || 'No records'
            });

            // Log sample of data to understand structure
            if (response.programs?.length > 0) {
                console.log('🔍 DatabaseService: Sample program records:', response.programs.slice(0, 3));
                console.log('📈 DatabaseService: Total programs received:', response.programs.length);
            }

            return response.programs || [];
        } catch (error) {
            console.error('❌ DatabaseService: Error fetching programs:', error);
            console.log('⚠️ DatabaseService: Backend API /programs is not available, returning empty array');
            return [];
        }
    }

    // Get filtered program progress - only unique tasks
    async getFilteredProgramProgress() {
        try {
            console.log('🔄 DatabaseService: Fetching filtered program progress...');
            const allProgress = await this.getAllProgramProgress();

            console.log('📊 DatabaseService: Raw progress data length:', allProgress.length);

            // Filter out duplicate tasks and get unique tasks only
            const uniqueTasks = new Map();

            allProgress.forEach((item: any) => {
                // Use program_id + checklist_id combination for unique tasks per program
                const taskKey = `${item.programId || item.program_id}_${item.checklistId || item.checklist_id}`;

                if (!uniqueTasks.has(taskKey)) {
                    uniqueTasks.set(taskKey, item);
                }
            });

            const filteredTasks = Array.from(uniqueTasks.values());

            console.log('✅ DatabaseService: Filtered unique tasks:', {
                originalCount: allProgress.length,
                filteredCount: filteredTasks.length,
                duplicatesRemoved: allProgress.length - filteredTasks.length
            });

            return filteredTasks;
        } catch (error) {
            console.error('❌ DatabaseService: Error filtering program progress:', error);
            return [];
        }
    }

    // Get all users from database
    async getAllUsers() {
        try {
            const response = await httpClient.get('/users');
            console.log('📊 DatabaseService: Users response:', response);
            return response.users || [];
        } catch (error) {
            console.error('Error fetching users:', error);
            return [];
        }
    }

    // Get all programs as tasks from programs table
    async getAllChecklistItems() {
        try {
            // Use programs endpoint
            const response = await httpClient.get('/programs');
            const programs = response.programs || [];
            // Convert programs to checklist-like format
            return programs.map((program: any, index: number) => ({
                id: `program_${program.id}`,
                title: program.title || `Program ${program.id}`,
                description: program.description || `Program ${program.id} description`,
                status: program.status || 'pending',
                assignedTo: program.assignedTo || 'System',
                dueDate: program.dueDate || null,
                createdAt: program.createdAt || new Date().toISOString(),
                updatedAt: program.updatedAt || new Date().toISOString(),
                program_type: '1', // Session type
                keyDeliverable: program.title || `Program ${program.id}`,
                taskName: program.title || `Program ${program.id}`,
                programId: program.id,
                program_id: program.id
            }));
        } catch (error) {
            console.error('Error fetching programs:', error);
            return [];
        }
    }

    // Get database statistics with real-time data
    async getDatabaseStats() {
        try {
            console.log('🔄 DatabaseService: Fetching real-time data...');

            const [programs, progress, users] = await Promise.all([
                this.getAllPrograms(),
                this.getFilteredProgramProgress(), // Use filtered progress instead of all progress
                this.getAllUsers()
            ]);

            console.log('📊 DatabaseService: Data fetched successfully:', {
                programs: programs.length,
                progress: progress.length,
                users: users.length
            });

            // Calculate statistics
            const sessionPrograms = programs.filter((p: any) => p.program_type === 'sessions' || !p.program_type);
            const workshopPrograms = programs.filter((p: any) => p.program_type === 'workshops');

            // Calculate program status from programs table (not progress table)
            const completedPrograms = programs.filter((p: any) => p.status === 'completed').length;
            const pendingPrograms = programs.filter((p: any) => p.status === 'pending').length;
            const inProgressPrograms = programs.filter((p: any) => p.status === 'in_progress').length;

            // Task statistics from progress table
            const pendingTasks = progress.filter((p: any) => p.status === 'pending');
            const inProgressTasks = progress.filter((p: any) => p.status === 'in-progress');
            const completedTasks = progress.filter((p: any) => p.status === 'completed');

            console.log('📈 DatabaseService: Statistics calculated:', {
                totalPrograms: programs.length,
                completedPrograms,
                pendingPrograms,
                inProgressPrograms,
                totalTasks: progress.length,
                pendingTasks: pendingTasks.length,
                inProgressTasks: inProgressTasks.length,
                completedTasks: completedTasks.length
            });

            // Debug: Log all programs and their statuses
            console.log('🔍 DatabaseService: All programs with statuses:', programs.map((p: any) => ({ id: p.id, title: p.title, status: p.status })));

            // Debug: Log programs with in-progress status
            const inProgressProgramsList = programs.filter((p: any) => p.status === 'in_progress');
            console.log('🔍 DatabaseService: In-progress programs found:', inProgressProgramsList);

            // Debug: Check for different status variations
            const statusVariations = Array.from(new Set(programs.map((p: any) => p.status)));
            console.log('🔍 DatabaseService: All unique statuses found:', statusVariations);

            return {
                totalPrograms: programs.length,
                completedPrograms, // From programs table
                pendingPrograms,   // From programs table
                inProgressPrograms, // From programs table
                sessionPrograms: sessionPrograms.length,
                workshopPrograms: workshopPrograms.length,
                totalUsers: users.length,
                totalTasks: progress.length,
                pendingTasks: pendingTasks.length,
                inProgressTasks: inProgressTasks.length,
                completedTasks: completedTasks.length,
                programs,
                progress,
                users
            };
        } catch (error) {
            console.error('❌ DatabaseService: Error fetching database stats:', error);
            return {
                totalPrograms: 0,
                completedPrograms: 0,
                pendingPrograms: 0,
                inProgressPrograms: 0,
                sessionPrograms: 0,
                workshopPrograms: 0,
                totalUsers: 0,
                totalTasks: 0,
                pendingTasks: 0,
                inProgressTasks: 0,
                completedTasks: 0,
                programs: [],
                progress: [],
                users: []
            };
        }
    }

    // Convert programs to tasks format
    convertProgramsToTasks(programs: any[], progress: any[]) {
        return programs.map(program => {
            const programProgress = progress.filter(p => p.programId === program.id);
            const completedCount = programProgress.filter(p => p.status === 'completed').length;
            const inProgressCount = programProgress.filter(p => p.status === 'in-progress').length;
            const totalCount = programProgress.length;

            // Determine category with proper type casting
            let category: 'session' | 'workshop' = 'session';
            if (program.program_type === 'workshops') {
                category = 'workshop';
            } else if (program.program_type === 'sessions') {
                category = 'session';
            } else {
                category = 'session'; // Default to session for other types
            }

            // Determine priority with proper type casting
            let priority: 'high' | 'medium' | 'low' = 'medium';
            if (program.priority === 'high') {
                priority = 'high';
            } else if (program.priority === 'low') {
                priority = 'low';
            } else {
                priority = 'medium';
            }

            // Determine status with proper type casting
            let status: 'pending' | 'in-progress' | 'completed' = 'pending';
            if (program.status === 'completed') {
                status = 'completed';
            } else if (program.status === 'in-progress') {
                status = 'in-progress';
            } else {
                status = 'pending';
            }

            return {
                id: program.id.toString(),
                title: program.title || program.name || 'Untitled Program',
                description: program.description || 'No description available',
                category: category,
                priority: priority,
                status: status,
                assignedTo: program.assignedTo || 'Unassigned',
                assignedToRole: 'Program Manager',
                dueDate: program.dueDate || program.endDate || '',
                createdDate: program.createdAt || '',
                lastUpdated: program.updatedAt || '',
                progress: totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0,
                comments: 0,
                attachments: 0,
                isStarred: false,
                programId: program.id.toString(),
                programName: program.title || program.name || 'Untitled Program',
                totalProgress: totalCount,
                completedProgress: completedCount,
                inProgressCount: inProgressCount
            };
        });
    }

    // Convert checklist items to tasks format for All Tasks column
    convertChecklistItemsToTasks(checklistItems: any[]) {
        return checklistItems.map((item, index) => {
            // Determine category based on program type
            let category: 'session' | 'workshop' = 'session';
            if (item.program_type === '2' || item.program_type === 2) {
                category = 'workshop';
            } else if (item.program_type === '1' || item.program_type === 1) {
                category = 'session';
            } else {
                category = 'session'; // Default to session for other types
            }

            // Map status from database to frontend format
            let status: 'pending' | 'in-progress' | 'completed' = 'pending';
            if (item.status === 'completed') {
                status = 'completed';
            } else if (item.status === 'in-progress' || item.status === 'in_progress') {
                status = 'in-progress';
            } else {
                status = 'pending';
            }

            return {
                id: `checklist_${item.id}`,
                title: item.keyDeliverable || item.taskName || item.title || 'Untitled Task',
                description: `Checklist item: ${item.keyDeliverable || item.taskName || item.title}`,
                category: category,
                priority: 'medium' as 'high' | 'medium' | 'low',
                status: status,
                assignedTo: item.assignedTo || item.assigned_to || 'System',
                assignedToRole: 'Checklist Item',
                dueDate: item.dueDate || item.due_date || '',
                createdDate: item.createdAt || item.created_at || new Date().toISOString(),
                lastUpdated: item.updatedAt || item.updated_at || new Date().toISOString(),
                progress: item.status === 'completed' ? 100 : 0,
                comments: 0,
                attachments: 0,
                isStarred: false,
                programId: item.programId || item.program_id || item.program_type,
                programName: `Program ${item.programId || item.program_id || item.program_type}`,
                isChecklistItem: true,
                checklistCategoryId: item.checklistCategoryId || item.checklist_category_id
            };
        });
    }

    // Get only actual tasks from programs table (not program_progress)
    async getActualTasks() {
        try {
            console.log('🔄 DatabaseService: Fetching actual tasks from programs table...');

            // Get all programs first
            const programs = await this.getAllPrograms();
            console.log('📊 DatabaseService: Programs fetched:', programs.length);

            // Get programs as tasks from programs table instead of program_progress
            const progress = await this.getAllProgramProgress();
            console.log('📊 DatabaseService: Programs as tasks fetched:', progress.length);

            // Convert programs to tasks format
            const tasks = this.convertProgressToTasks(progress);

            console.log('✅ DatabaseService: Actual tasks converted:', {
                programs: programs.length,
                progress: progress.length,
                tasks: tasks.length
            });

            return {
                programs,
                progress,
                tasks
            };
        } catch (error) {
            console.error('❌ DatabaseService: Error fetching actual tasks:', error);
            return {
                programs: [],
                progress: [],
                tasks: []
            };
        }
    }

    // Convert programs to tasks format
    convertProgressToTasks(progress: any[]) {
        console.log('🔍 DatabaseService: Converting programs to tasks, sample item:', progress[0]);

        return progress.map((item: any, index: number) => {
            // Determine category based on program type - check actual program data
            let category: 'session' | 'workshop' = 'session';

            // Check if we have program type information - be more flexible with field names
            const programType = item.programType || item.program_type || item.type;
            const programId = item.programId || item.program_id;

            // Log first few items for debugging
            if (index < 3) {
                console.log(`🔍 DatabaseService: Item ${index} program type fields:`, {
                    programType: item.programType,
                    program_type: item.program_type,
                    type: item.type,
                    programId: programId,
                    program_id: item.program_id,
                    resolved: programType,
                    fullItem: item
                });
            }

            // Use programType to determine category - programType 1 = Session, programType 2 = Workshop
            if (programType === 2 || programType === '2') {
                category = 'workshop';
                console.log(`🔍 DatabaseService: Item ${index} - WORKSHOP detected:`, { programId, programType });
            } else if (programType === 1 || programType === '1') {
                category = 'session';
                console.log(`🔍 DatabaseService: Item ${index} - SESSION detected:`, { programId, programType });
            } else {
                // Default to session for most programs
                category = 'session';
                console.log(`🔍 DatabaseService: Item ${index} - DEFAULT to session:`, { programId, programType });
            }

            // Log category assignment for first few items
            if (index < 3) {
                console.log(`🔍 DatabaseService: Item ${index} assigned category:`, category);
            }

            // Determine status with proper type casting
            let status: 'pending' | 'in-progress' | 'completed' = 'pending';
            if (item.status === 'completed') {
                status = 'completed';
            } else if (item.status === 'in-progress') {
                status = 'in-progress';
            } else {
                status = 'pending';
            }

            return {
                id: item.id || item.programId || Math.random().toString(36).substr(2, 9),
                title: item.taskName || item.title || `Program ${item.programId || item.id}`,
                description: item.description || `Program: ${item.taskName || item.title}`,
                category: category,
                priority: 'medium' as 'high' | 'medium' | 'low',
                status: status,
                assignedTo: item.assignedTo || item.assigned_to || 'System',
                assignedToRole: 'Program Manager',
                dueDate: item.dueDate || item.due_date || '',
                createdDate: item.createdAt || item.created_at || new Date().toISOString(),
                lastUpdated: item.updatedAt || item.updated_at || new Date().toISOString(),
                progress: item.status === 'completed' ? 100 : item.status === 'in-progress' ? 50 : 0,
                comments: 0,
                attachments: 0,
                isStarred: false,
                programId: item.programId || item.program_id || item.program_type,
                programName: `Program ${item.programId || item.program_id}`,
                isChecklistItem: true,
                checklistCategoryId: item.checklistCategoryId || item.checklist_category_id
            };
        });
    }

    // Real-time refresh method
    async refreshData() {
        console.log('🔄 DatabaseService: Refreshing data in real-time...');
        try {
            // Get fresh data
            const freshData = await this.getActualTasks();

            console.log('✅ DatabaseService: Data refreshed successfully:', {
                programs: freshData.programs.length,
                progress: freshData.progress.length,
                tasks: freshData.tasks.length
            });

            return freshData;
        } catch (error) {
            console.error('❌ DatabaseService: Error refreshing data:', error);
            throw error;
        }
    }

    // Get system status
    async getUserGrowthData() {
        try {
            console.log('🔄 DatabaseService: Fetching user growth data...');
            const response = await httpClient.get('/programs/user-growth');
            console.log('📊 DatabaseService: User growth data response:', response);
            return response.data || [];
        } catch (error) {
            console.error('❌ DatabaseService: Error fetching user growth data:', error);
            return [];
        }
    }


    async getRecentActivities() {
        try {
            console.log('🔄 DatabaseService: Fetching recent activities...');
            const response = await httpClient.get('/recent-activities');
            console.log('📊 DatabaseService: Recent activities response:', response);
            console.log('📊 DatabaseService: Response success:', response.success);
            console.log('📊 DatabaseService: Activities array:', response.activities);
            console.log('📊 DatabaseService: Activities length:', response.activities?.length || 0);
            return response.activities || [];
        } catch (error) {
            console.error('❌ DatabaseService: Error fetching recent activities:', error);
            console.error('❌ DatabaseService: Error details:', error instanceof Error ? error.message : 'Unknown error');
            return [];
        }
    }

    // Get program progress tasks for a specific program
    async getProgramProgressTasks(programId: string | number) {
        try {
            console.log('🔄 DatabaseService: Fetching program progress tasks for program:', programId);
            const response = await httpClient.get(`/programs/${programId}/progress`);
            console.log('📊 DatabaseService: Program progress response:', response);
            return response || [];
        } catch (error) {
            console.error('❌ DatabaseService: Error fetching program progress tasks:', error);
            return [];
        }
    }
}

export const databaseService = new DatabaseService();
